<template>
    <v-menu v-model="accountMenuOpen" offset-x nudge-bottom="18">
        <template v-slot:activator="{ on: menuOn }">
            <v-list-item v-on="menuOn" :input-value="accountMenuOpen" active-class="primary--text" e2e-toolbar-account>
                <v-list-item-icon>
                    <v-tooltip right :disabled="!collapsed">
                        <template v-slot:activator="{ on: tooltipOn, attrs }">
                            <v-icon v-bind="attrs" v-on="tooltipOn">mdi-account-circle-outline</v-icon>
                        </template>
                        <span>{{ userFullName || 'Account' }}</span>
                    </v-tooltip>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title>{{ userFullName || 'Account' }}</v-list-item-title>
                    <v-list-item-subtitle>{{ userInfo ? userInfo.email : '' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-icon>
                    <v-icon>mdi-chevron-right</v-icon>
                </v-list-item-icon>
            </v-list-item>
        </template>
        <v-list nav dense min-width="200">
            <v-list-item @click="openSettings()">
                <v-list-item-icon>
                    <v-icon>mdi-account-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title e2e-toolbar-settings>Account Settings</v-list-item-title>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item v-if="isAccountManager" @click="openResourceAndUsage()">
                <v-list-item-icon>
                    <v-icon>mdi-account-multiple-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title e2e-toolbar-account-management>Account Management</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isSysadmin" @click="openSysadmin()">
                <v-list-item-icon>
                    <v-icon>mdi-shield-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title e2e-toolbar-sysadmin>Sysadmin</v-list-item-title>
            </v-list-item>
            <v-divider v-if="isAccountManager || isSysadmin" class="my-2"></v-divider>
            <v-list-item @click="openMonitoring()">
                <v-list-item-icon>
                    <v-icon>mdi-chart-box-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title e2e-toolbar-monitoring>Resources</v-list-item-title>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item href="https://docs.nuvolos.com/" target="_blank" rel="noopener noreferrer">
                <v-list-item-icon>
                    <v-icon>mdi-book-open-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title>Docs</v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon>
                    <v-icon small>mdi-open-in-new</v-icon>
                </v-list-item-icon>
            </v-list-item>
            <v-list-item v-if="canShowContactSupport" @click="showIntercomChat()">
                <v-list-item-icon>
                    <v-icon>mdi-help-circle-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Contact support</v-list-item-title>
                <v-list-item-icon>
                    <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small v-bind="attrs" v-on="on" @click.stop>mdi-information-outline</v-icon>
                        </template>
                        <span>Start a conversation with our support team for account or technical issues.</span>
                    </v-tooltip>
                </v-list-item-icon>
            </v-list-item>
            <v-list-item @click="showFrigadeAssistant()">
                <v-list-item-icon>
                    <v-icon>mdi-forum-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Ask anything</v-list-item-title>
                <v-list-item-icon>
                    <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small v-bind="attrs" v-on="on" @click.stop>mdi-information-outline</v-icon>
                        </template>
                        <span>Get quick guidance and answers for common tasks in the platform.</span>
                    </v-tooltip>
                </v-list-item-icon>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item v-if="!isPWA()" @click="installPWA()">
                <v-list-item-icon>
                    <v-icon>mdi-monitor-arrow-down-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Install desktop app</v-list-item-title>
            </v-list-item>
            <v-divider class="my-2"></v-divider>
            <v-list-item @click="logout()">
                <v-list-item-icon>
                    <v-icon>mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title e2e-toolbar-logout>Logout</v-list-item-title>
            </v-list-item>
        </v-list>
        <v-dialog v-model="pwaDialog" width="500" persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    <div></div>
                    <v-spacer></v-spacer>
                    <v-btn @click="pwaDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-alert prominent type="info" text>
                    <h3 class="text-h6 mb-2">Browser not supported</h3>
                    <p>
                        To install the desktop application, please log into Nuvolos using Google Chrome or Microsoft Edge, the current browser does not support
                        this functionality.
                    </p>
                    <v-card-actions></v-card-actions>
                </v-alert>
            </v-card>
        </v-dialog>
    </v-menu>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { showFrigadeAssistant } from '@/utils/frigade'
import pwa from '@/mixins/pwa'
import { intercomMethods } from '@/mixins/intercom'
import { enumsData } from '@/mixins/enums'

export default {
    mixins: [pwa, intercomMethods, enumsData],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            pwaDialog: false,
            accountMenuOpen: false
        }
    },
    computed: {
        ...mapState(['userInfo']),
        ...mapGetters(['isSysadmin', 'userFullName']),
        ...mapGetters('orgStore', ['isOrgManager', 'isOrgFaculty']),
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'currentSpaceType']),
        ...mapState('userStore', ['isAccountManager']),
        isCourseSpace() {
            return Number(this.currentSpaceType) === this.spaceTypes.EDUCATION_SPACE
        },
        canShowContactSupport() {
            if (!this.$appConfig.VUE_APP_INTERCOM_ID) {
                return false
            }

            if (!this.isOrgManager && !this.isOrgFaculty) {
                return false
            }

            if (this.isCourseSpace && !this.isSpaceAdmin) {
                return false
            }

            return true
        }
    },
    methods: {
        openSettings() {
            this.$router.push({ name: 'user-profile' })
        },
        openResourceAndUsage() {
            this.$router.push({ name: 'user-resources' })
        },
        openMonitoring() {
            this.$router.push({ name: 'reporting-dashboard' })
        },
        openSysadmin() {
            this.$router.push({ name: 'admin-overview' })
        },
        logout() {
            this.$auth.logout()
        },
        showFrigadeAssistant() {
            showFrigadeAssistant()
        },
        installPWA() {
            const isChromium = !!window.chrome
            if (isChromium) {
                this.install()
            } else {
                this.pwaDialog = true
            }
        }
    },
    watch: {
        userInfo: {
            async handler(user) {
                if (user) {
                    this.$store.dispatch('userStore/fetchAccountManagerStatus')
                }
            },
            immediate: true
        }
    }
}
</script>
